// StudyDost — App.js v2
// frontend/src/App.js

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import ChatPage from './pages/ChatPage';
import HistoryPage from './pages/HistoryPage';
import PlansPage from './pages/PlansPage';
import ProfilePage from './pages/ProfilePage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LandingPage from './pages/LandingPage';
import ParentPage from './pages/ParentPage';
import ParentDashboard from './pages/ParentDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import TeacherOnboard from './pages/TeacherOnboard';
import HomeworkPrint from './pages/HomeworkPrint';
import MyReportPage from './pages/MyReportPage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import RefundPage from './pages/RefundPage';
import BooksPage from './pages/BooksPage';

// Components
import BottomNav from './components/BottomNav';
import LoadingScreen from './components/LoadingScreen';

// ── Parent role prompt for logged-in students ──
function ParentRolePrompt() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFF', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ background: '#FFFFFF', borderRadius: 24, padding: '32px 24px', maxWidth: 400, width: '100%', textAlign: 'center', boxShadow: '0 8px 40px rgba(0,0,0,0.1)' }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>👨‍👩‍👦</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>Parent Portal</h2>
        <p style={{ fontSize: 14, color: '#64748B', lineHeight: 1.6, marginBottom: 28 }}>
          Aap abhi student account se logged in hain.<br />
          Parent dashboard ke liye alag number se login karein.
        </p>
        <button
          style={{ width: '100%', background: 'linear-gradient(135deg, #4F46E5, #7C3AED)', color: '#FFFFFF', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}
          onClick={() => { logout(); navigate('/parent/login'); }}
        >
          Parent Number se Login Karein
        </button>
        <button
          style={{ width: '100%', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
          onClick={() => navigate('/chat')}
        >
          Student Dashboard Par Jao
        </button>
      </div>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30000 },
  },
});

function ProtectedRoute({ children, requiredRole }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (requiredRole && !user.roles?.includes(requiredRole)) {
    // Redirect to appropriate login page for the required role
    if (requiredRole === 'PARENT') return <Navigate to="/parent" replace />;
    if (requiredRole === 'TEACHER') return <Navigate to="/teacher" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
}

// Smart gateway for /parent:
// - Not logged in → show parent login page
// - Logged in as PARENT → go to dashboard
// - Logged in as STUDENT → offer to become parent too
function ParentGateway() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <LoginPage defaultRole="PARENT" redirectTo="/parent/dashboard" />;
  // Check roles array or legacy single role
  const isParent = user.roles?.includes('PARENT') || user.activeRole === 'PARENT';
  if (isParent) return <Navigate to="/parent/dashboard" replace />;
  // Logged in as student — show option to switch to parent
  return <ParentRolePrompt />;
}


// Smart gateway for /teacher:
// - Not logged in → show TeacherOnboard
// - Logged in as TEACHER → go to dashboard
// - Logged in as STUDENT only → show TeacherOnboard (they can add role)
function TeacherGateway() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  const isTeacher = user?.roles?.includes('TEACHER') || user?.activeRole === 'TEACHER';
  if (isTeacher) return <Navigate to="/teacher/dashboard" replace />;
  return <TeacherOnboard />;
}

function AppShell({ children }) {
  return (
    <div className="app-shell">
      <main className="main-content">{children}</main>
      <BottomNav />
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/" element={
        !user ? <LandingPage /> :
        (user.roles?.includes('TEACHER') || user.activeRole === 'TEACHER') ? <Navigate to="/teacher/dashboard" replace /> :
        (user.roles?.includes('PARENT') || user.activeRole === 'PARENT') ? <Navigate to="/parent/dashboard" replace /> :
        <Navigate to="/chat" replace />
      } />
      <Route path="/login" element={
        !user ? <LoginPage /> :
        (user.roles?.includes('TEACHER') || user.activeRole === 'TEACHER') ? <Navigate to="/teacher/dashboard" replace /> :
        (user.roles?.includes('PARENT') || user.activeRole === 'PARENT') ? <Navigate to="/parent/dashboard" replace /> :
        <Navigate to="/chat" replace />
      } />
      <Route path="/payment/success" element={<PaymentSuccessPage />} />

      {/* ── Parent ── */}
      {/* /parent — smart redirect based on auth state */}
      <Route path="/parent" element={<ParentGateway />} />
      <Route path="/parent/login" element={
        user ? <Navigate to="/parent/dashboard" replace /> :
        <LoginPage defaultRole="PARENT" redirectTo="/parent/dashboard" />
      } />
      <Route path="/parent/dashboard" element={
        <ProtectedRoute requiredRole="PARENT">
          <AppShell><ParentDashboard /></AppShell>
        </ProtectedRoute>
      } />

      {/* ── Teacher ── */}
      {/* Teacher login/onboard page */}
      <Route path="/teacher" element={<TeacherGateway />} />
      <Route path="/teacher/setup" element={<TeacherOnboard />} />
      <Route path="/teacher/homework/print/:id" element={<HomeworkPrint />} />
      <Route path="/teacher/login" element={
        <LoginPage defaultRole="TEACHER" redirectTo="/teacher/dashboard" />
      } />
      {/* Teacher dashboard — requires TEACHER role */}
      <Route path="/teacher/dashboard" element={
        <ProtectedRoute requiredRole="TEACHER">
          <AppShell><TeacherDashboard /></AppShell>
        </ProtectedRoute>
      } />

      {/* ── Student (protected) ── */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <AppShell><ChatPage /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/history" element={
        <ProtectedRoute>
          <AppShell><HistoryPage /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/plans" element={
        <ProtectedRoute>
          <AppShell><PlansPage /></AppShell>
        </ProtectedRoute>
      } />
      <Route path="/books" element={<BooksPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/my-report" element={
        <ProtectedRoute><AppShell><MyReportPage /></AppShell></ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppShell><ProfilePage /></AppShell>
        </ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#1E40AF',
                color: '#fff',
                borderRadius: '12px',
                fontFamily: "'DM Sans', sans-serif",
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
